/**
 * Valuation Page
 * Two top-level tabs: Market Data (IR Curves + Fwd Point Curves + Spot Rates sub-toggle)
 * and Forward Valuation.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../components/auth/AuthContext';
import ValuationCurves from './ValuationCurves';
import ValuationFwdPointCurves from './ValuationFwdPointCurves';
import ValuationSpotRates from './ValuationSpotRates';
import ValuationPositions from './ValuationPositions';
import SystemDateDisplay from '../../components/common/SystemDateDisplay';
import { useSystemDate } from '../../hooks/useSystemDate';
import '../admin/Accounting.css';

type ValuationTab = 'marketData' | 'positions';
type MarketDataSubTab = 'curves' | 'fwdPointCurves' | 'spotRates';

const subTabStyle = (active: boolean, position: 'left' | 'middle' | 'right'): React.CSSProperties => ({
  padding: '0.35rem 1rem',
  fontSize: '0.85rem',
  fontWeight: 600,
  cursor: 'pointer',
  borderRadius: position === 'left' ? '6px 0 0 6px' : position === 'right' ? '0 6px 6px 0' : '0',
  border: `1px solid ${active ? 'var(--accent-blue)' : 'var(--border-color)'}`,
  borderLeft: position !== 'left' ? 'none' : undefined,
  background: active ? 'var(--accent-blue)' : 'transparent',
  color: active ? '#ffffff' : 'var(--text-secondary)',
  transition: 'all 0.2s ease',
});

const Valuation: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const clientId = user?.organization?.id || user?.id;
  const systemDateResult = useSystemDate(clientId);
  const [activeTab, setActiveTab] = useState<ValuationTab>('positions');
  const [marketDataSubTab, setMarketDataSubTab] = useState<MarketDataSubTab>('curves');

  const navigateToIrCurves = () => { setActiveTab('marketData'); setMarketDataSubTab('curves'); };
  const navigateToFwdPoints = () => { setActiveTab('marketData'); setMarketDataSubTab('fwdPointCurves'); };
  const navigateToSpotRates = () => { setActiveTab('marketData'); setMarketDataSubTab('spotRates'); };

  return (
    <div className="accounting-page">
      {/* Header */}
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1>{t('navigation.valuation')}</h1>
        <SystemDateDisplay clientId={clientId} systemDateData={systemDateResult} />
      </div>

      {/* Top-level tabs */}
      <div className="accounting-tabs">
        <button
          className={`tab-button ${activeTab === 'positions' ? 'active' : ''}`}
          onClick={() => setActiveTab('positions')}
        >
          {t('valuation.positions.title')}
        </button>
        <button
          className={`tab-button ${activeTab === 'marketData' ? 'active' : ''}`}
          onClick={() => setActiveTab('marketData')}
        >
          {t('valuation.marketData.title', 'Market Data')}
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'marketData' && (
          <div>
            {/* Sub-toggle: IR Curves | Fwd Point Curves | Spot Rates */}
            <div style={{ display: 'flex', marginBottom: '1rem' }}>
              <button onClick={() => setMarketDataSubTab('curves')} style={subTabStyle(marketDataSubTab === 'curves', 'left')}>
                {t('valuation.curves.title')}
              </button>
              <button onClick={() => setMarketDataSubTab('fwdPointCurves')} style={subTabStyle(marketDataSubTab === 'fwdPointCurves', 'middle')}>
                {t('valuation.fwdPointCurves.title')}
              </button>
              <button onClick={() => setMarketDataSubTab('spotRates')} style={subTabStyle(marketDataSubTab === 'spotRates', 'right')}>
                {t('valuation.spotRates.title', 'Tipos de Cambio')}
              </button>
            </div>

            {marketDataSubTab === 'curves' && <ValuationCurves systemDate={systemDateResult.systemDate} />}
            {marketDataSubTab === 'fwdPointCurves' && <ValuationFwdPointCurves systemDate={systemDateResult.systemDate} />}
            {marketDataSubTab === 'spotRates' && <ValuationSpotRates systemDate={systemDateResult.systemDate} />}
          </div>
        )}
        {activeTab === 'positions' && (
          <ValuationPositions
            systemDate={systemDateResult.systemDate}
            onNavigateToIrCurves={navigateToIrCurves}
            onNavigateToFwdPoints={navigateToFwdPoints}
            onNavigateToSpotRates={navigateToSpotRates}
          />
        )}
      </div>
    </div>
  );
};

export default Valuation;
